import { useSetReviewerInfo, useUserStore } from '~/entities/user'
import { useAreFeatureFlagsReady, useIsFeatureEnabled } from '~/shared/lib/feature-flags'

type ReportAccess = { isLoading: true; isAllowed: false } | { isLoading: false; isAllowed: boolean }

/**
 * Gates the report on the feature flag AND onshore status.
 *
 * `isLoading` matters: every input resolves asynchronously and all of them
 * default to falsy, so without it a permitted onshore user would see the 404
 * page flash before access was granted. Anything that fails to resolve leaves
 * the gate closed.
 */
export function useReportAccess(): ReportAccess {
  const isFeatureEnabled = useIsFeatureEnabled('2026-08-medistream-report')
  const areFlagsReady = useAreFeatureFlagsReady()
  // PageBase already calls this; the shared TanStack Query cache dedupes the
  // request, and calling it here keeps the gate working if the page is ever
  // mounted outside PageBase.
  const { isLoading: isReviewerLoading } = useSetReviewerInfo()
  const userInfo = useUserStore((state) => state.userInfo)
  const isOnshore = useUserStore((state) => state.isOnshore)

  // The reviewer lookup is keyed by email and stays disabled until Okta's
  // userInfo lands — while disabled it reports `isLoading: false`, so waiting
  // on the email too is what keeps this from reading as "denied" too early.
  if (!areFlagsReady || !userInfo?.email || isReviewerLoading) {
    return { isLoading: true, isAllowed: false }
  }

  return { isLoading: false, isAllowed: isFeatureEnabled && isOnshore }
}
